import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLang } from '../../context/LanguageContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { adminListApplications, adminUpdateApplication, adminGetApplication, adminDeleteApplication, adminApplicationContractUrl, getFreezones } from '../../api';
import ApplicationView from '../../components/ApplicationView';
import { toast } from '../../hooks/use-toast';
import { businessActivities } from '../../mock';
import { Search, Eye, Trash2, Save, Building2, Globe, Mail, Phone, User, Calendar, DollarSign, Printer, Receipt, FileDown, FileSignature } from 'lucide-react';

const statusOptions = [
  { value: 'in_review', color: 'bg-amber-500/15 text-amber-400 border-amber-400/30' },
  { value: 'documents_pending', color: 'bg-orange-500/15 text-orange-400 border-orange-400/30' },
  { value: 'approved', color: 'bg-blue-500/15 text-blue-400 border-blue-400/30' },
  { value: 'rejected', color: 'bg-rose-500/15 text-rose-400 border-rose-400/30' }
];

const statusLabelsMap = {
  in_review: { en: 'In Review', ar: 'قيد المراجعة' },
  documents_pending: { en: 'Documents Pending', ar: 'مستندات معلقة' },
  approved: { en: 'Approved', ar: 'موافق عليه' },
  rejected: { en: 'Rejected', ar: 'مرفوض' }
};

const AdminApplications = () => {
  const { lang } = useLang();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: params.get('status') || '', type: params.get('type') || '', search: '' });
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [zones, setZones] = useState([]);
  const printRef = useRef(null);

  useEffect(() => { getFreezones().then(setZones).catch(() => {}); }, []);

  const doPrint = (app) => {
    if (!app) return;
    setPrinting(app);
    setTimeout(() => {
      const el = printRef.current;
      if (!el) return;
      const w = window.open('', '_blank', 'width=900,height=1000');
      if (!w) { window.print(); return; }
      const head = Array.from(document.head.querySelectorAll('link[rel=stylesheet], style'))
        .map((n) => n.outerHTML).join('\n');
      const html = `<!DOCTYPE html><html lang="${lang}" dir="${lang === 'ar' ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>${app.id}</title>${head}<style>
        body{margin:0;padding:24px;background:#fff;color:#f8fafc;}
        .application-printable{box-shadow:none !important;max-width:100% !important;margin:0 !important;}
        @page { size: A4; margin: 12mm; }
        @media print { body { padding:0; } }
      </style></head><body>${el.outerHTML}<script>window.onload=()=>{setTimeout(()=>{window.print();},400);};window.onafterprint=()=>{window.close();};</script></body></html>`;
      w.document.open(); w.document.write(html); w.document.close();
      setTimeout(() => setPrinting(null), 800);
    }, 60);
  };

  const load = async () => {
    setLoading(true);
    try {
      const p = {};
      if (filters.status) p.status = filters.status;
      if (filters.type) p.type = filters.type;
      if (filters.search) p.search = filters.search;
      const data = await adminListApplications(p);
      setApps(data || []);
    } catch (err) {
      toast({ title: 'Error loading', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters.status, filters.type]);

  const exportPdf = async () => {
    await load();
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.type) p.set('type', filters.type);
    p.set('_t', String(Date.now()));
    window.open(`${base}/api/admin/applications/export.pdf?${p.toString()}`, '_blank');
    toast({ title: lang === 'ar' ? '📄 يتم توليد PDF…' : '📄 Generating PDF…' });
  };

  const openDetails = async (app) => {
    try {
      const detail = await adminGetApplication(app.id);
      setSelected(detail);
      setEditing({
        status: detail.status,
        progress: detail.progress,
        admin_notes: detail.admin_notes || '',
        company_name: detail.company_name || '',
        capital: detail.capital || '',
        visas: detail.visas || 1,
        office_type: detail.office_type || 'flexi',
        full_name: detail.full_name || '',
        email: detail.email || '',
        phone: detail.phone || '',
        activity: detail.activity || '',
        type: detail.type || 'mainland',
        zone_code: detail.zone_code || '',
        created_at: (detail.created_at || '').toString().split('T')[0],
        pricing_notes: detail.pricing_notes || '',
        shareholders: detail.shareholders || 1,
        estimate: { license: 0, office: 0, visa: 0, admin: 0, tax: 0, ...(detail.estimate || {}) }
      });
    } catch {
      toast({ title: 'Load failed', variant: 'destructive' });
    }
  };

  const saveChanges = async () => {
    if (!selected || !editing) return;
    try {
      const payload = { ...editing, created_at: editing.created_at || undefined, zone_code: editing.type === 'freezone' ? (editing.zone_code || undefined) : undefined };
      const updated = await adminUpdateApplication(selected.id, payload);
      setSelected({ ...selected, ...updated, zone_name: updated.zone_code ? (zoneName(zones.find((z) => z.code === updated.zone_code), lang) || selected.zone_name) : null });
      toast({ title: lang === 'ar' ? 'تم الحفظ' : 'Saved' });
      load();
    } catch {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await adminDeleteApplication(id);
      toast({ title: lang === 'ar' ? 'تم الحذف' : 'Deleted' });
      setSelected(null);
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'الطلبات' : 'Applications'}</h1>
          <p className="text-sm text-[#111827]/60 mt-2">{apps.length} {lang === 'ar' ? 'طلب' : 'applications'}</p>
        </div>
        <Button onClick={exportPdf} variant="outline" className="rounded-md h-10 px-4 text-xs border-[#2563eb]/40 text-[#2563eb] hover:bg-[#2563eb]/8" data-testid="apps-export-pdf-btn">
          <FileDown className="w-4 h-4 me-2" /> {lang === 'ar' ? 'كشف PDF' : 'Export PDF'}
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl p-4 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-[#2563eb]/70 absolute top-1/2 -translate-y-1/2 start-3" />
          <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder={lang === 'ar' ? 'بحث (اسم، إيميل، مرجع)' : 'Search (name, email, ref)'} className="h-10 ps-9 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
        </div>
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[180px] h-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
            <SelectItem value="all" className="text-[#111827]">{lang === 'ar' ? 'كل الحالات' : 'All statuses'}</SelectItem>
            {statusOptions.map((s) => <SelectItem key={s.value} value={s.value} className="text-[#111827]">{statusLabelsMap[s.value][lang]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.type || 'all'} onValueChange={(v) => setFilters({ ...filters, type: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[160px] h-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
            <SelectItem value="all" className="text-[#111827]">{lang === 'ar' ? 'الكل' : 'All'}</SelectItem>
            <SelectItem value="mainland" className="text-[#111827]">{lang === 'ar' ? 'براً' : 'Mainland'}</SelectItem>
            <SelectItem value="freezone" className="text-[#111827]">{lang === 'ar' ? 'منطقة حرة' : 'Freezone'}</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} className="gold-button rounded-md h-10 px-5 text-xs">{lang === 'ar' ? 'تحديث' : 'Refresh'}</Button>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-[#111827]/60">…</div>
        : apps.length === 0 ? <div className="p-10 text-center text-[#111827]/60">{lang === 'ar' ? 'لا توجد طلبات' : 'No applications'}</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#2563eb]/8 border-b border-[#2563eb]/15">
                <tr className="text-[10px] uppercase tracking-widest text-[#2563eb] text-start">
                  <th className="p-3 text-start">{lang === 'ar' ? 'المرجع' : 'Ref'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الشركة' : 'Company'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'النوع' : 'Type'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'المنطقة' : 'Zone'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'المالك' : 'Owner'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'التقدم' : 'Progress'}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => {
                  const s = statusOptions.find((o) => o.value === a.status) || statusOptions[0];
                  return (
                    <tr key={a.id} className="border-b border-[#2563eb]/8 hover:bg-[#2563eb]/4 transition-colors">
                      <td className="p-3 font-mono text-xs text-[#3b82f6]">{a.id}</td>
                      <td className="p-3 text-[#0a0a0a]">{a.company_name}</td>
                      <td className="p-3">
                        <div className="inline-flex items-center gap-1 text-xs text-[#111827]/70">
                          {a.type === 'mainland' ? <Building2 className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                          {a.type}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-[#111827]/70">{a.zone_name || '—'}</td>
                      <td className="p-3 text-xs text-[#111827]/70">{a.full_name}<br /><span className="text-[10px] text-[#111827]/50">{a.email}</span></td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${s.color}`}>{statusLabelsMap[a.status]?.[lang] || a.status}</span>
                      </td>
                      <td className="p-3 w-24">
                        <Progress value={a.progress || 15} className="h-1 bg-[#2563eb]/10" />
                        <div className="text-[10px] text-[#111827]/50 mt-1">{a.progress || 15}%</div>
                      </td>
                      <td className="p-3 flex items-center gap-1">
                        <Button onClick={() => openDetails(a)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={lang === 'ar' ? 'عرض / تعديل' : 'View / Edit'}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => navigate(`/admin/receipts?fromApp=${a.id}`)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={lang === 'ar' ? 'إصدار إيصال لهذا العميل' : 'Issue receipt for this client'} data-testid={`create-receipt-${a.id}`}><Receipt className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => doPrint(a)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={lang === 'ar' ? 'طباعة / PDF' : 'Print / PDF'}><Printer className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => window.open(adminApplicationContractUrl(a.id), '_blank')} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={lang === 'ar' ? 'عقد التأسيس PDF' : 'Formation contract PDF'} data-testid={`contract-pdf-${a.id}`}><FileSignature className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0a0a0a] font-display text-2xl">{selected?.company_name}</DialogTitle>
            <div className="text-xs text-[#2563eb] font-mono">{selected?.id}</div>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label={lang === 'ar' ? 'النوع' : 'Type'} value={selected.type} icon={selected.type === 'mainland' ? Building2 : Globe} />
                <Info label={lang === 'ar' ? 'المنطقة' : 'Zone'} value={selected.zone_name || '—'} icon={Building2} />
                <Info label={lang === 'ar' ? 'النشاط' : 'Activity'} value={selected.activity} />
                <Info label={lang === 'ar' ? 'رأس المال' : 'Capital'} value={`${selected.capital || '—'} AED`} icon={DollarSign} />
                <Info label={lang === 'ar' ? 'التأشيرات' : 'Visas'} value={selected.visas} />
                <Info label={lang === 'ar' ? 'المكتب' : 'Office'} value={selected.office_type} />
                <Info label={lang === 'ar' ? 'المالك' : 'Owner'} value={selected.full_name} icon={User} />
                <Info label={lang === 'ar' ? 'الإيميل' : 'Email'} value={selected.email} icon={Mail} />
                <Info label={lang === 'ar' ? 'الهاتف' : 'Phone'} value={selected.phone} icon={Phone} />
                <Info label={lang === 'ar' ? 'تاريخ الإرسال' : 'Submitted'} value={(selected.created_at || '').toString().split('T')[0]} icon={Calendar} />
              </div>
              {selected.notes && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#2563eb] mb-1">{lang === 'ar' ? 'ملاحظات العميل' : 'Customer Notes'}</div>
                  <div className="bg-[#f8fafc] rounded-md p-3 text-sm text-[#111827]/80">{selected.notes}</div>
                </div>
              )}

              <div className="border-t border-[#2563eb]/15 pt-4 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'تعديل الطلب' : 'Edit Application'}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'نوع التأسيس' : 'Setup Type'}</label>
                    <Select value={editing?.type || 'mainland'} onValueChange={(v) => setEditing({ ...editing, type: v, zone_code: v === 'mainland' ? '' : editing?.zone_code })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="edit-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                        <SelectItem value="mainland" className="text-[#111827]">{lang === 'ar' ? 'البر الرئيسي (Mainland)' : 'Mainland'}</SelectItem>
                        <SelectItem value="freezone" className="text-[#111827]">{lang === 'ar' ? 'منطقة حرة (Free Zone)' : 'Free Zone'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'المنطقة الحرة' : 'Free Zone'}</label>
                    <Select value={editing?.zone_code || ''} onValueChange={(v) => setEditing({ ...editing, zone_code: v })} disabled={editing?.type !== 'freezone'}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] disabled:opacity-50" data-testid="edit-zone"><SelectValue placeholder={editing?.type === 'freezone' ? (lang === 'ar' ? 'اختر المنطقة' : 'Select zone') : (lang === 'ar' ? 'لا ينطبق' : 'N/A')} /></SelectTrigger>
                      <SelectContent className="bg-[#ffffff] border-[#2563eb]/25 max-h-72">
                        {zones.map((z) => <SelectItem key={z.code} value={z.code} className="text-[#111827]">{zoneName(z, lang)} ({z.code.toUpperCase()})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'نوع النشاط' : 'Business Activity'}</label>
                    <Select value={editing?.activity || ''} onValueChange={(v) => setEditing({ ...editing, activity: v })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="edit-activity"><SelectValue placeholder={lang === 'ar' ? 'اختر النشاط' : 'Select activity'} /></SelectTrigger>
                      <SelectContent className="bg-[#ffffff] border-[#2563eb]/25 max-h-72">
                        {businessActivities.map((a) => <SelectItem key={a.id} value={a.id} className="text-[#111827]">{a[lang] || a.en}</SelectItem>)}
                        {editing?.activity && !businessActivities.some((a) => a.id === editing.activity) && <SelectItem value={editing.activity} className="text-[#111827]">{editing.activity}</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'تاريخ الإرسال' : 'Submission Date'}</label>
                    <Input type="date" value={editing?.created_at || ''} onChange={(e) => setEditing({ ...editing, created_at: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="edit-created-at" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'اسم الشركة' : 'Company Name'}</label>
                    <Input value={editing?.company_name || ''} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'رأس المال' : 'Capital'}</label>
                    <Input value={editing?.capital || ''} onChange={(e) => setEditing({ ...editing, capital: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'التأشيرات' : 'Visas'}</label>
                    <Input type="number" min="0" value={editing?.visas || 0} onChange={(e) => setEditing({ ...editing, visas: parseInt(e.target.value, 10) || 0 })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'الشركاء' : 'Shareholders'}</label>
                    <Input type="number" min="1" value={editing?.shareholders || 1} onChange={(e) => setEditing({ ...editing, shareholders: parseInt(e.target.value, 10) || 1 })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'اسم المالك' : 'Owner Name'}</label>
                    <Input value={editing?.full_name || ''} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">Email</label>
                    <Input type="email" value={editing?.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'الهاتف' : 'Phone'}</label>
                    <Input value={editing?.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'نوع المكتب' : 'Office Type'}</label>
                    <Select value={editing?.office_type || 'flexi'} onValueChange={(v) => setEditing({ ...editing, office_type: v })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                        <SelectItem value="flexi" className="text-[#111827]">Flexi Desk</SelectItem>
                        <SelectItem value="private" className="text-[#111827]">Private Office</SelectItem>
                        <SelectItem value="executive" className="text-[#111827]">Executive Suite</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="pt-3 border-t border-[#2563eb]/10" data-testid="quote-editor">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold">{lang === 'ar' ? 'تفاصيل الأسعار (تنتقل للفاتورة والعقد)' : 'Quote / Pricing (flows to receipt & contract)'}</label>
                    <span className="text-xs text-[#111827]/50">{selected.estimate_edited_by ? `${lang === 'ar' ? 'آخر تعديل' : 'Edited by'} ${selected.estimate_edited_by}` : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      ['license', lang === 'ar' ? 'رسوم الرخصة والتسجيل' : 'License & Registration Fees'],
                      ['office', `${lang === 'ar' ? 'المكتب' : 'Office'} (${{ flexi: 'Flexi Desk', private: 'Private Office', executive: 'Executive Suite' }[editing?.office_type] || '-'})`],
                      ['visa', lang === 'ar' ? 'معالجة التأشيرات' : 'Visa Processing'],
                      ['admin', lang === 'ar' ? 'الأتعاب المهنية والمعاملات' : 'Professional & PRO Fees'],
                      ['tax', lang === 'ar' ? 'التسجيل الضريبي' : 'Tax Registration'],
                    ].map(([k, label]) => (
                      <div key={k} className="flex items-center gap-3">
                        <span className="flex-1 text-sm text-[#111827]/75">{label}</span>
                        <div className="relative w-40">
                          <Input type="number" min="0" step="50" value={editing?.estimate?.[k] ?? 0} onChange={(e) => setEditing({ ...editing, estimate: { ...editing.estimate, [k]: parseFloat(e.target.value) || 0 } })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] text-end pe-12 tabular-nums" data-testid={`quote-input-${k}`} />
                          <span className="absolute inset-y-0 end-3 flex items-center text-[10px] text-[#111827]/40">AED</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 border-t border-[#2563eb]/15">
                      <span className="text-xs uppercase tracking-widest text-[#2563eb] font-bold">{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                      <span className="font-display text-xl text-[#0a0a0a] tabular-nums" data-testid="quote-editor-total">{['license', 'office', 'visa', 'admin', 'tax'].reduce((s, k) => s + (Number(editing?.estimate?.[k]) || 0), 0).toLocaleString('en-US')} AED</span>
                    </div>
                    <div className="pt-2">
                      <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'تفاصيل الأسعار (تظهر في الفاتورة والعقد)' : 'Pricing details (shown on receipt & contract)'}</label>
                      <Textarea rows={3} value={editing?.pricing_notes || ''} onChange={(e) => setEditing({ ...editing, pricing_notes: e.target.value })} placeholder={lang === 'ar' ? 'مثال: الرسوم تشمل رسوم الجهة الحكومية، ولا تشمل التصديقات والترجمة…' : 'e.g. Fees include authority charges; attestations and translations excluded…'} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="pricing-notes-input" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#2563eb]/10">
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'الحالة' : 'Status'}</label>
                    <Select value={editing?.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                        {statusOptions.map((s) => <SelectItem key={s.value} value={s.value} className="text-[#111827]">{statusLabelsMap[s.value][lang]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'التقدم %' : 'Progress %'}</label>
                    <Input type="number" min="0" max="100" value={editing?.progress || 0} onChange={(e) => setEditing({ ...editing, progress: parseInt(e.target.value, 10) || 0 })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#111827]/60 block mb-1">{lang === 'ar' ? 'ملاحظات الأدمن (داخلية)' : 'Admin Notes (internal)'}</label>
                  <Textarea rows={3} value={editing?.admin_notes || ''} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => remove(selected.id)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent border-rose-400/30 text-rose-400"><Trash2 className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'حذف' : 'Delete'}</Button>
            <Button onClick={() => navigate(`/admin/receipts?fromApp=${selected.id}`)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent"><Receipt className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إصدار إيصال' : 'Issue Receipt'}</Button>
            <Button onClick={() => doPrint(selected)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent"><Printer className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'طباعة / PDF' : 'Print / PDF'}</Button>
            <Button onClick={() => window.open(adminApplicationContractUrl(selected.id), '_blank')} className="rounded-md h-10 px-4 text-xs bg-[#0a0a0a] hover:bg-[#111827] text-white" data-testid="contract-pdf-detail-btn"><FileSignature className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'طباعة عقد التأسيس' : 'Print formation contract'}</Button>
            <Button onClick={saveChanges} className="gold-button rounded-md h-10 px-5 text-xs"><Save className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'حفظ' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Off-screen printable render */}
      {printing && (
        <div style={{ position: 'fixed', left: '-10000px', top: 0 }} aria-hidden="true">
          <ApplicationView ref={printRef} application={printing} lang={lang} />
        </div>
      )}
    </div>
  );
};

const zoneName = (z, lang) => (!z ? '' : typeof z.name === 'object' ? (z.name[lang] || z.name.en || '') : (z.name || ''));

const Info = ({ label, value, icon: Icon }) => (
  <div className="bg-[#f8fafc]/50 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 flex items-center gap-1.5 mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      <span>{label}</span>
    </div>
    <div className="text-sm text-[#0a0a0a]">{value || '—'}</div>
  </div>
);

export default AdminApplications;
